// src/components/BonusScoreAdmin.js
import React, { useState, useEffect, useMemo } from 'react';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import './BonusScoreAdmin.css';
import { getTeamDisplayLabel } from '../utils/teamDisplayLabel';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const BonusScoreAdmin = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [teams, setTeams] = useState([]);
  const [puzzles, setPuzzles] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null); // { teamId, puzzleId } while saving

  // Add form
  const [addTeamId, setAddTeamId] = useState('');
  const [addPuzzleId, setAddPuzzleId] = useState('');
  const [addValue, setAddValue] = useState('');
  const [bonusTableExport, setBonusTableExport] = useState(null); // null | { mode: 'print'|'copy' }
  const [bonusExportCols, setBonusExportCols] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length > 0) {
          setSelectedEvent((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEvent) {
      setTeams([]);
      setPuzzles([]);
      setScores([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    async function fetchData() {
      try {
        const [teamsSnap, puzzlesSnap, scoresSnap] = await Promise.all([
          getDocs(query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))),
          getDocs(query(collection(firestore, 'bonusPuzzles'), where('eventId', '==', selectedEvent))),
          getDocs(query(collection(firestore, 'bonusScores'), where('eventId', '==', selectedEvent)))
        ]);
        setTeams(teamsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPuzzles(puzzlesSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => Number(a.number) - Number(b.number)));
        setScores(scoresSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError('Failed to load data: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedEvent]);

  const bonusScoreExportColumnDefs = useMemo(() => {
    const puzzleCols = puzzles.map((p) => ({
      id: `p_${p.id}`,
      label: `#${p.number} ${p.title || 'Puzzle'}`.trim(),
      default: true,
    }));
    return [{ id: 'team', label: 'Team', default: true }, ...puzzleCols];
  }, [puzzles]);

  const bonusScoreExportRows = useMemo(() => {
    return teams.map((team) => {
      const row = { team: getTeamDisplayLabel(team) };
      puzzles.forEach((p) => {
        const s = scores.find((x) => x.teamId === team.id && x.puzzleId === p.id);
        const v = s != null ? Number(s.value) : null;
        row[`p_${p.id}`] = v != null && !Number.isNaN(v) ? String(v) : '';
      });
      return row;
    });
  }, [teams, puzzles, scores]);

  useEffect(() => {
    if (!bonusTableExport) return;
    setBonusExportCols(
      new Set(bonusScoreExportColumnDefs.filter((c) => c.default).map((c) => c.id))
    );
  }, [bonusTableExport, bonusScoreExportColumnDefs]);

  const openBonusTableExport = (mode) => {
    if (!selectedEvent || !teams.length) {
      window.alert('Select an event with teams to export.');
      return;
    }
    setBonusTableExport({ mode });
  };

  const resetBonusExportCols = () => {
    setBonusExportCols(
      new Set(bonusScoreExportColumnDefs.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runBonusTableExport = (selectedColumnIds) => {
    if (!bonusTableExport || !selectedEvent) return;
    const { mode } = bonusTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : bonusExportCols;
    const active = bonusScoreExportColumnDefs.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = bonusScoreExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const ev = events.find((e) => e.id === selectedEvent);
    const evTitle = ev?.title || selectedEvent || 'Bonus scores';
    const sub = 'Bonus scores by team';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} team(s), ${puzzles.length} puzzle column(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setBonusTableExport(null);
  };

  const getScoreDoc = (teamId, puzzleId) => scores.find(s => s.teamId === teamId && s.puzzleId === puzzleId);
  const getScoreValue = (teamId, puzzleId) => {
    const s = getScoreDoc(teamId, puzzleId);
    return s != null ? Number(s.value) : null;
  };

  const saveCell = async (teamId, puzzleId, newValue) => {
    const num = newValue === '' || newValue === null ? null : Number(newValue);
    const existing = getScoreDoc(teamId, puzzleId);
    setSaving({ teamId, puzzleId });

    try {
      console.log('[BonusScoreAdmin] saveCell start', {
        eventId: selectedEvent,
        teamId,
        puzzleId,
        newValue,
        parsedValue: num,
        existingDocId: existing?.id || null,
        writeCollection: 'bonusScores',
      });
      if (num === null || num === 0) {
        if (existing) {
          await deleteDoc(doc(firestore, 'bonusScores', existing.id));
          setScores(prev => prev.filter(s => s.id !== existing.id));
          console.log('[BonusScoreAdmin] saveCell deleted existing score', { docId: existing.id });
        }
      } else {
        const payload = {
          eventId: selectedEvent,
          teamId,
          puzzleId,
          value: num,
          timestamp: new Date()
        };
        if (existing) {
          await updateDoc(doc(firestore, 'bonusScores', existing.id), { value: num });
          setScores(prev => prev.map(s => s.id === existing.id ? { ...s, value: num } : s));
          console.log('[BonusScoreAdmin] saveCell updated score', { docId: existing.id, value: num });
        } else {
          const ref = await addDoc(collection(firestore, 'bonusScores'), payload);
          setScores(prev => [...prev, { id: ref.id, ...payload }]);
          console.log('[BonusScoreAdmin] saveCell created score', { docId: ref.id, ...payload });
        }
      }
    } catch (err) {
      console.error('Score save failed:', err);
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleAddScore = async (e) => {
    e.preventDefault();
    if (!selectedEvent) {
      setError('Select an event before adding a score.');
      return;
    }
    if (!addTeamId || !addPuzzleId) {
      setError('Select a team and a puzzle.');
      return;
    }
    const num = Number(addValue);
    if (isNaN(num) || num < 0 || num > 999) {
      setError('Enter a valid score between 0 and 999.');
      return;
    }
    const existing = getScoreDoc(addTeamId, addPuzzleId);
    if (existing) {
      setError('This team already has a score for this puzzle. Edit it in the table.');
      return;
    }
    setError('');
    try {
      const payload = {
        eventId: selectedEvent,
        teamId: addTeamId,
        puzzleId: addPuzzleId,
        value: num,
        timestamp: new Date()
      };
      console.log('[BonusScoreAdmin] Add score BEFORE Firestore write (addDoc)', {
        formState: { selectedEvent, addTeamId, addPuzzleId, addValue },
        payload,
        collectionPath: 'bonusScores',
        note: 'Event year (e.g. 2027) is only in payload.eventId, not in collection path',
      });
      const ref = await addDoc(collection(firestore, 'bonusScores'), payload);
      console.log('[BonusScoreAdmin] Add score AFTER Firestore write', {
        docPath: `bonusScores/${ref.id}`,
        payload,
      });
      setScores(prev => [...prev, { id: ref.id, eventId: selectedEvent, teamId: addTeamId, puzzleId: addPuzzleId, value: num }]);
      setAddValue('');
      setAddPuzzleId('');
    } catch (err) {
      console.error('Score save failed:', err);
      setError('Failed to add score: ' + err.message);
    }
  };

  return (
    <div className="bonus-score-admin">
      <h2>Edit bonus scores</h2>
      {error && <p className="bonus-score-admin-error">{error}</p>}

      <div className="bonus-score-admin-toolbar">
        <label>
          Event:
          <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
            <option value="">Select event</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : !selectedEvent ? (
        <p>Select an event.</p>
      ) : (
        <>
          <div className="bonus-score-admin-add">
            <h3>Add a bonus score</h3>
            <form onSubmit={handleAddScore}>
              <label>
                Team
                <select value={addTeamId} onChange={e => setAddTeamId(e.target.value)} required>
                  <option value="">Select team</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{getTeamDisplayLabel(t)}</option>
                  ))}
                </select>
              </label>
              <label>
                Puzzle
                <select value={addPuzzleId} onChange={e => setAddPuzzleId(e.target.value)} required>
                  <option value="">Select puzzle</option>
                  {puzzles.map(p => (
                    <option key={p.id} value={p.id}>#{p.number} {p.title || ''}</option>
                  ))}
                </select>
              </label>
              <label>
                Value
                <input type="number" min="0" step="1" value={addValue} onChange={e => setAddValue(e.target.value)} placeholder="Points" />
              </label>
              <button type="submit">Add score</button>
            </form>
          </div>

          <div className="bonus-score-admin-scores-header">
            <h3>Scores by team and puzzle</h3>
            <div className="bonus-score-admin-export-actions">
              <button
                type="button"
                className="btn-fm-print-outline"
                onClick={() => openBonusTableExport('print')}
                disabled={!teams.length}
              >
                Print table
              </button>
              <AdminCopyMenu
                ariaLabel="Copy bonus scores table"
                disabled={!teams.length}
                items={[
                  {
                    label: 'Copy table…',
                    showCopiedFeedback: false,
                    onSelect: () => openBonusTableExport('copy'),
                  },
                ]}
              />
            </div>
          </div>
          <p className="bonus-score-admin-hint">Click a cell to edit. Clear the value and blur to remove a score.</p>
          <div className="bonus-score-admin-table-wrap">
            <table className="bonus-score-admin-table">
              <thead>
                <tr>
                  <th>Team</th>
                  {puzzles.map(p => (
                    <th key={p.id} title={p.title || ''}>#{p.number}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map(team => (
                  <tr key={team.id}>
                    <td className="team-cell">{getTeamDisplayLabel(team)}</td>
                    {puzzles.map(puzzle => {
                      const current = getScoreValue(team.id, puzzle.id);
                      const isSaving = saving && saving.teamId === team.id && saving.puzzleId === puzzle.id;
                      return (
                        <td key={puzzle.id} className="score-cell">
                          <ScoreCell
                            current={current}
                            isSaving={isSaving}
                            onSave={v => saveCell(team.id, puzzle.id, v)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {bonusTableExport && bonusScoreExportColumnDefs.length > 0 && (
        <AdminColumnExportModal
          mode={bonusTableExport.mode}
          columns={bonusScoreExportColumnDefs}
          selectedIds={bonusExportCols}
          onChangeSelected={setBonusExportCols}
          onCancel={() => setBonusTableExport(null)}
          onConfirm={runBonusTableExport}
          onResetDefaults={resetBonusExportCols}
        />
      )}
    </div>
  );
};

function ScoreCell({ current, isSaving, onSave }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(current !== null ? String(current) : '');

  const display = current !== null ? current : '';

  const handleBlur = () => {
    setEditing(false);
    const trimmed = local.trim();
    const num = trimmed === '' ? null : Number(trimmed);
    if (num !== null && (isNaN(num) || num < 0 || num > 999)) return;
    if (num === null && current === null) return;
    if (num !== current) onSave(trimmed === '' ? null : num);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  useEffect(() => {
    setLocal(current !== null ? String(current) : '');
  }, [current]);

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        max="999"
        step="1"
        className="score-input"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
      />
    );
  }

  return (
    <span
      className={`score-display ${isSaving ? 'saving' : ''}`}
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setEditing(true); }}
    >
      {isSaving ? '…' : display}
    </span>
  );
}

export default BonusScoreAdmin;
